'use client'

import PropTypes from 'prop-types'
import MUITypography from '@mui/material/Typography'
import MUIStack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useClientStore } from '@repo/store/useClientStore'
import { InfoIcon } from '@repo/ui/InfoIcon'
import { WarningIcon } from '@repo/ui/WarningIcon'
import { LockIcon } from '@repo/ui/LockIcon'
import { useState, useEffect } from 'react'
import MUITooltip from '@mui/material/Tooltip'

export const ClientMatterDetailsPill = ({
  clientNo,
  matterNo,
  sx,
  clientMatterNo,
  isUserRestricted = false,
  suppressRestrictedTooltip = false,
  highlightIfHighRisk = false
}) => {
  const theme = useTheme()
  const [isHighRisk, setIsHighRisk] = useState(false)
  const [matter, setMatter] = useState(null)

  if (!clientMatterNo) {
    clientMatterNo = matterNo ? `${clientNo}-${matterNo}` : clientNo
  }

  const { setMatterDrawerIsOpen, matters } = useMattersStore()
  const { clients } = useClientStore()

  useEffect(() => {
    if (matters && clientMatterNo && matters[clientMatterNo]) {
      const client = clients[clientNo]
      const matter = matters[clientMatterNo]
      setMatter(matter)

      const isHighRisk = matter?.highRisk || client?.highRisk || false
      setIsHighRisk((highlightIfHighRisk && isHighRisk) || false)
    }
  }, [matters, clients, clientMatterNo, clientNo, highlightIfHighRisk])

  const handleClick = (e) => {
    e.stopPropagation()
    if (isUserRestricted || matter?.isUserRestricted) return
    setMatterDrawerIsOpen(true, clientNo, clientMatterNo)
  }

  if (!clientMatterNo && !clientNo) return null

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', position: 'relative', height: '22px', ...sx }}
      data-testid="ClientMatterDetailsPill"
    >
      <MUITooltip
        title={
          (isUserRestricted || matter?.isUserRestricted) && !suppressRestrictedTooltip
            ? 'You are not authorised to view this matter'
            : ''
        }
        arrow
        placement="top"
      >
        <MUIStack
          onClick={(e) => handleClick(e)}
          flexDirection={'row'}
          alignItems={'center'}
          sx={{
            '&:hover': { backgroundColor: isHighRisk ? 'error.main' : 'background.slate100' },
            height: sx?.height || '22px',
            width: sx?.width || '100%',
            color: 'text.primary',
            fontSize: '0.75rem',
            padding: '0 4px 1px 6px',
            borderRadius: '3px',
            backgroundColor: isHighRisk ? 'error.main' : 'background.slate100',
            position: 'relative',
            top: '-3px',
            cursor: isUserRestricted || matter?.isUserRestricted ? 'not-allowed' : 'pointer',
            flexWrap: 'nowrap',
            justifyContent: 'center'
          }}
        >
          <MUITypography
            variant="body1"
            sx={{
              cursor: isUserRestricted || matter?.isUserRestricted ? 'not-allowed' : 'pointer',
              fontSize: '0.75rem',
              fontWeight: 500,
              mb: 0,
              color: isHighRisk ? 'white' : 'inherit'
            }}
          >
            {clientMatterNo}
          </MUITypography>

          {isHighRisk ? (
            <WarningIcon
              sx={{
                color: 'white',
                width: '10px',
                height: '10px',
                marginLeft: 1,
                marginRight: 1
              }}
            />
          ) : null}

          {!isHighRisk && (isUserRestricted || matter?.isUserRestricted) ? (
            <LockIcon
              sx={{
                color: theme?.palette?.support?.navy || 'black',
                width: '10px',
                height: '10px',
                marginLeft: 1,
                marginRight: 1
              }}
            />
          ) : null}

          {!isHighRisk && !(isUserRestricted || matter?.isUserRestricted) ? (
            <InfoIcon
              sx={{
                color: theme?.palette?.support?.navy || 'black',
                width: '10px',
                height: '10px',
                marginLeft: 1,
                marginRight: 1
              }}
            />
          ) : null}
        </MUIStack>
      </MUITooltip>
    </div>
  )
}

ClientMatterDetailsPill.propTypes = {
  clientNo: PropTypes.string,
  matterNo: PropTypes.any,
  clientMatterNo: PropTypes.string,
  isUserRestricted: PropTypes.bool,
  suppressRestrictedTooltip: PropTypes.bool,
  highlightIfHighRisk: PropTypes.bool,
  sx: PropTypes.object
}
